import type { PurchaseReturnResponse } from '@palaccount/contracts';
import { Banknote, CheckCheck, Pencil, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import { useCashAccountOptions } from '@/features/accounts/api';
import { sumAmounts } from '@/lib/money';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';

import { BILLS_KEY } from './api';
import { purchaseReturnSteps } from './purchase-steppers';
import { PurchasesTabs } from './purchases-tabs';
import {
  fetchPurchaseReturnsPage,
  PURCHASE_RETURNS_KEY,
  PURCHASE_RETURNS_SEARCHABLE,
  useCancelPurchaseReturn,
  usePostPurchaseReturn,
  usePurchaseReturn,
  usePurchaseReturns,
  usePurchaseReturnStats,
  useRefundPurchaseReturn,
} from './returns-api';

const STATUSES: PurchaseReturnResponse['status'][] = ['draft', 'posted', 'credited', 'refunded', 'cancelled'];
/**
 * PUR-04 §7 through the one palette: Draft grey, Posted blue (approving IS
 * posting here), Credited and Refunded green (settled — BR-PUR-04-01/-02),
 * Cancelled red-faded.
 */
const TONES = {
  draft: 'draft',
  posted: 'posted',
  credited: 'completed',
  refunded: 'completed',
  cancelled: 'cancelled',
} as const;

type PurchaseReturnStats = ReturnType<typeof usePurchaseReturnStats>['data'] & object;

const trimQty = (qty: string) => qty.replace(/\.?0+$/, '');

/**
 * PUR-04's list on the shared list-and-detail page (wave 3B): the debit
 * note's OWN number (DBN-####, BR-PUR-04-12), the bill it is against as a
 * chip, the register's tiles (the top reason is the one tile that is a
 * word — null draws a dash, never a guessed reason, BR-PUR-04-09), and the
 * note's own chain — Bill → Return → Stock out → Debit note / refund, greyed
 * where it does not apply. The acts: post a draft, record the supplier's
 * cash refund (BR-PUR-04-02, the treasurer's permission), print, cancel a
 * posted one (BR-PUR-04-01, reason required).
 */
export function PurchaseReturnsListPage() {
  const { t, i18n } = useTranslation();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [refundAccountId, setRefundAccountId] = useState('');
  const { options: cashOptions } = useCashAccountOptions();
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postReturn = usePostPurchaseReturn(selectedId);
  const refundReturn = useRefundPurchaseReturn(selectedId);
  const cancelReturn = useCancelPurchaseReturn(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: PurchaseReturnResponse['status']) => t(`preturn.status.${status}`);
  const chip = (note: PurchaseReturnResponse) => <StatusChip status={note.status} tones={TONES} ns="preturn" />;
  const exportColumns = [
    { header: t('preturn.colNo'), value: (n: PurchaseReturnResponse) => n.code },
    { header: t('common.date'), value: (n: PurchaseReturnResponse) => n.returnDate, kind: 'date' as const },
    { header: t('preturn.colAgainst'), value: (n: PurchaseReturnResponse) => n.billCode },
    { header: t('po.colSupplier'), value: (n: PurchaseReturnResponse) => n.partnerName },
    { header: t('returns.colReason'), value: (n: PurchaseReturnResponse) => t(`returns.reason.${n.reason}`) },
    { header: t('sales.colStatus'), value: (n: PurchaseReturnResponse) => statusLabel(n.status) },
    { header: t('sales.exportColCurrency'), value: (n: PurchaseReturnResponse) => n.currency },
    { header: t('preturn.colDebit'), value: (n: PurchaseReturnResponse) => n.grandTotal, kind: 'number' as const },
  ];
  const pdfRequest = (note: PurchaseReturnResponse) => ({ kind: 'purchase_return' as const, locale, subjectId: note.id });
  const toFilters = (filters: { status: PurchaseReturnResponse['status'] | ''; dateFrom: string; dateTo: string; partnerId: string }) => ({
    status: filters.status || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    partnerId: filters.partnerId || undefined,
  });

  const config: DocumentListConfig<PurchaseReturnResponse, PurchaseReturnResponse['status'], PurchaseReturnStats> = {
    titleKey: 'preturn.title',
    tabs: <PurchasesTabs />,
    /* BR-PERM-18: greyed with the reason, never hidden — the same gate the other purchasing lists wear. */
    newAction: { to: '/purchases/returns/new', labelKey: 'common.new', permission: 'purchasing.create', reasonKey: 'perm.cannot.createPurchaseReturns' },
    statuses: STATUSES,
    statusLabel,
    search: PURCHASE_RETURNS_SEARCHABLE ? { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' } : undefined,
    dateRange: true,
    partner: { kind: 'supplier' },
    useList: (filters, page, pageSize) => {
      const query = usePurchaseReturns(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'returns'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => usePurchaseReturn(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = usePurchaseReturnStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'monthValue', labelKey: 'preturn.tileMonthValue', format: 'money', value: (s) => s.returnsThisMonthValue, currency: (s) => s.currency },
        { key: 'monthCount', labelKey: 'preturn.tileMonthCount', format: 'count', value: (s) => s.count },
        { key: 'awaiting', labelKey: 'preturn.tileAwaiting', format: 'count', value: (s) => s.awaitingSupplierCredit, tone: 'warn' },
        { key: 'topReason', labelKey: 'preturn.tileTopReason', format: 'text', value: (s) => (s.topReason === null ? '—' : t(`returns.reason.${s.topReason}`)) },
      ],
    },
    card: (note) => ({
      code: note.code,
      chip: chip(note),
      title: note.partnerName,
      meta: (
        <>
          <bdi dir="ltr">{note.returnDate}</bdi> · {t(`returns.reason.${note.reason}`)}
        </>
      ),
      amount: { value: note.grandTotal, currency: note.currency },
    }),
    columns: { titleKey: 'po.colSupplier', metaKey: 'returns.colReason', amountKey: 'preturn.colDebit', statusKey: 'sales.colStatus' },
    emptyKey: 'preturn.empty',
    selectOneKey: 'preturn.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'purchase-returns',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchPurchaseReturnsPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="purchasing.approve"
          reasonKey="perm.cannot.postPurchaseReturns"
          invalidate={[PURCHASE_RETURNS_KEY, BILLS_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.purchaseReturns.post({ params: { returnId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="purchase-returns" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (note) => [
        {
          key: 'bill',
          to: `/purchases/bills/${note.billId}`,
          tone: 'muted',
          label: t('preturn.againstBill', { code: note.billCode }),
        },
      ],
      subline: (note) => (
        <>
          {note.partnerName} · {t(`returns.reason.${note.reason}`)}
          {note.reasonNote ? ` — ${note.reasonNote}` : null}
        </>
      ),
      steps: { labelKey: 'preturn.stepper', of: purchaseReturnSteps },
      /* "qty returned / billed" — the note's units against the bill lines they come off. */
      progress: (note) =>
        t('preturn.listProgress', {
          returned: trimQty(note.qtyReturned),
          billed: trimQty(sumAmounts(note.lines.map((line) => line.billedQty))),
        }),
      actions: (note) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/purchases/returns/${note.id}`}>
              <Pencil aria-hidden /> {note.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {note.status === 'draft' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.postPurchaseReturns">
              <Button onClick={() => void act(() => postReturn.mutateAsync())} disabled={busy}>
                <CheckCheck aria-hidden /> {t('preturn.actPost')}
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(note)} />
          {note.status === 'posted' ? (
            <>
              <NativeSelect
                aria-label={t('preturn.refundAccount')}
                value={refundAccountId}
                onChange={(event) => setRefundAccountId(event.target.value)}
                className="h-10 w-auto text-sm"
              >
                <option value="">{t('receipts.pickAccount')}</option>
                {cashOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </option>
                ))}
              </NativeSelect>
              <Gated permission="accounting.approve" reasonKey="perm.cannot.refundPurchaseReturns">
                <Button
                  variant="outline"
                  disabled={busy || !refundAccountId}
                  onClick={() => void act(() => refundReturn.mutateAsync(refundAccountId))}
                >
                  <Banknote aria-hidden /> {t('preturn.refund')}
                </Button>
              </Gated>
            </>
          ) : null}
          {note.status === 'posted' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.cancelPurchaseReturns">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('preturn.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {note.status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('preturn.listCancelTitle')}
              prompt={t('preturn.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelReturn.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (note) =>
          note.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{trimQty(line.qty)}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={note.currency} />,
              <Amount key="t" value={line.lineTotal} currency={note.currency} />,
            ],
          })),
      },
      totals: (note) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: note.subtotal, currency: note.currency },
          { value: note.taxAmount, currency: note.currency },
          { value: note.grandTotal, currency: note.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
